<script lang="ts">
  /**
   * The solo questions on a `PickerPage`, reading down: which mode (two
   * tiles), the goal and its target (Sprint only: two short-word choices, so
   * two rows with a pill segment at the right), and how hard (four cards,
   * each saying what it means). Drawn by the solo New game picker and by the
   * setup of a round on one device, so both offer the same choices
   * (`nav/mode-choices.ts`). `choices` is bound: every answer replaces it.
   */
  import {
    ChoiceTiles,
    Group,
    RadioCards,
    Row,
    SectionLabel,
    SegmentedControl,
  } from '@artibens/app-shell';
  import type { ChoiceTileOption, SegmentOption } from '@artibens/app-shell';
  import { SPRINT_LINE_GOALS, SPRINT_TIME_GOALS_MIN } from '@interlock/modes';

  import { hardnessCards } from '../copy/pickers.js';
  import type { GoalKind, ModeChoices, PickedMode } from './mode-choices.js';

  let { choices = $bindable() }: { choices: ModeChoices } = $props();

  const modeOptions: ChoiceTileOption<PickedMode>[] = [
    { value: 'sprint', name: 'Sprint', note: 'Lines, or the clock' },
    { value: 'survival', name: 'Survival', note: 'The floor keeps rising' },
  ];

  const goalOptions: SegmentOption<GoalKind>[] = [
    { value: 'lines', label: 'Lines' },
    { value: 'time', label: 'Timed' },
  ];

  const lineOptions: SegmentOption[] = SPRINT_LINE_GOALS.map((n) => ({
    value: String(n),
    label: String(n),
  }));

  const timeOptions: SegmentOption[] = SPRINT_TIME_GOALS_MIN.map((n) => ({
    value: String(n),
    label: `${String(n)} min`,
  }));
</script>

{#snippet goalControl()}
  <SegmentedControl
    label="Goal type"
    value={choices.goalKind}
    options={goalOptions}
    onchange={(v) => {
      choices = { ...choices, goalKind: v };
    }}
  />
{/snippet}

{#snippet targetControl()}
  {#if choices.goalKind === 'lines'}
    <SegmentedControl
      label="Line target"
      value={String(choices.lines)}
      options={lineOptions}
      onchange={(v) => {
        choices = { ...choices, lines: Number(v) };
      }}
    />
  {:else}
    <SegmentedControl
      label="Time limit"
      value={String(choices.timeMin)}
      options={timeOptions}
      onchange={(v) => {
        choices = { ...choices, timeMin: Number(v) };
      }}
    />
  {/if}
{/snippet}

<SectionLabel variant="picker" as="h3">Which mode?</SectionLabel>
<ChoiceTiles
  label="Game mode"
  value={choices.mode}
  options={modeOptions}
  onchange={(v) => {
    choices = { ...choices, mode: v };
  }}
/>

{#if choices.mode === 'sprint'}
  <SectionLabel variant="picker" as="h3">The goal</SectionLabel>
  <Group>
    <Row label="Goal" trailing={goalControl} />
    <Row label={choices.goalKind === 'lines' ? 'Target' : 'Limit'} trailing={targetControl} />
  </Group>
{/if}

<SectionLabel variant="picker" as="h3">How hard?</SectionLabel>
<RadioCards
  label="Hardness"
  value={choices.hardness}
  options={hardnessCards(choices.mode === 'survival')}
  onchange={(v) => {
    choices = { ...choices, hardness: v };
  }}
/>
